// client/src/components/PublicProfile/PublicPlayerInfo.js
import React from 'react';
import { 
  User, Crown, Star, Award, Trophy, Target, Calendar, 
  TrendingUp, Zap, Shield, Users, Activity 
} from 'lucide-react';

const PublicPlayerInfo = ({ profileData, isOwnProfile, t }) => {
  // Get role information
  const getRoleInfo = (role) => {
    const roleMap = {
      'main_admin': { 
        display: t('mainAdmin', 'Main Admin'), 
        className: 'main-admin',
        icon: Crown,
        color: '#ef4444'
      },
      'supervisor': { 
        display: t('supervisor', 'Supervisor'), 
        className: 'supervisor',
        icon: Star,
        color: '#8b5cf6'
      },
      'approver': { 
        display: t('approver', 'Approver'), 
        className: 'approver',
        icon: Award,
        color: '#3b82f6'
      },
      'player': { 
        display: t('player', 'Player'), 
        className: 'player',
        icon: Users,
        color: '#10b981'
      }
    };
    return roleMap[role] || roleMap['player'];
  };

  // Get level title based on level
  const getLevelTitle = (level) => {
    if (level >= 100) return t('legend', 'Legend');
    if (level >= 90) return t('sage', 'Sage');
    if (level >= 80) return t('mythic', 'Mythic');
    if (level >= 70) return t('grandmaster', 'Grandmaster');
    if (level >= 60) return t('master', 'Master');
    if (level >= 50) return t('expert', 'Expert');
    if (level >= 40) return t('professional', 'Professional');
    if (level >= 30) return t('advanced', 'Advanced');
    if (level >= 20) return t('intermediate', 'Intermediate');
    if (level >= 10) return t('novice', 'Novice');
    return t('beginner', 'Beginner');
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format last online
  const formatLastOnline = (dateString) => {
    if (!dateString) return t('unknown', 'Unknown');
    
    const now = new Date();
    const lastOnline = new Date(dateString);
    const diffInMinutes = Math.floor((now - lastOnline) / (1000 * 60));
    
    if (diffInMinutes < 1) return t('justNow', 'Just now');
    if (diffInMinutes < 60) return `${diffInMinutes} ${t('minutesAgo', 'minutes ago')}`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} ${t('hoursAgo', 'hours ago')}`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} ${t('daysAgo', 'days ago')}`;
    
    return formatDate(dateString);
  };

  const roleInfo = getRoleInfo(profileData.role);
  const RoleIcon = roleInfo.icon;
  const levelTitle = getLevelTitle(profileData.currentLevel);

  return (
    <div className="public-player-info">
      {/* Main Player Card */}
      <div className="player-main-card">
        <div className="player-avatar-section">
          <div className="player-avatar">
            <User className="w-8 h-8" />
          </div>
          <div className={`status-dot ${profileData.isOnline ? 'online' : 'offline'}`}></div>
        </div>
        
        <div className="player-identity">
          <h2 className="player-name">
            {profileData.inGameName}
            {isOwnProfile && (
              <span className="own-badge">{t('you', '(You)')}</span>
            )}
          </h2>
          <div className="player-username">@{profileData.username}</div>
          
          <div className={`role-badge ${roleInfo.className}`}>
            <RoleIcon className="w-3 h-3" style={{ color: roleInfo.color }} />
            <span>{roleInfo.display}</span>
          </div>
        </div>
      </div>

      {/* Level and Progress */}
      {profileData.showLevelProgress !== false && (
        <div className="level-section">
          <div className="level-header">
            <div className="level-main">
              <div className="level-number">
                <Zap className="w-5 h-5" />
                {profileData.currentLevel}
              </div>
              <div className="level-info">
                <div className="level-title">{levelTitle}</div>
                <div className="level-exp">
                  {profileData.totalExp?.toLocaleString() || 0} {t('totalExp', 'Total EXP')}
                </div>
              </div>
            </div>
            
            {profileData.rank && (
              <div className="level-rank">
                <Trophy className="w-4 h-4" />
                <span>#{profileData.rank} {t('globally', 'Globally')}</span>
              </div>
            )}
          </div>
          
          {/* Recent milestones */}
          {profileData.levelMilestones && profileData.levelMilestones.length > 0 && (
            <div className="level-milestones">
              <h4 className="milestones-title">{t('recentMilestones', 'Recent Milestones')}</h4>
              <div className="milestones-list">
                {profileData.levelMilestones.slice(-3).reverse().map((milestone, index) => (
                  <div key={index} className="milestone-item">
                    <Award className="w-3 h-3" />
                    <span>
                      {t('reachedLevel', 'Reached Level')} {milestone.level} - {milestone.title}
                    </span>
                    <span className="milestone-date">
                      {new Date(milestone.reachedAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Stats Grid */}
      <div className="quick-stats-grid">
        <div className="quick-stat-item points">
          <div className="stat-icon">
            <Target className="w-5 h-5" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{profileData.points?.toLocaleString() || 0}</div>
            <div className="stat-label">{t('points', 'Points')}</div>
          </div>
        </div>

        <div className="quick-stat-item wins">
          <div className="stat-icon">
            <Trophy className="w-5 h-5" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{profileData.wins}</div>
            <div className="stat-label">{t('wins', 'Wins')}</div>
          </div>
        </div>

        <div className="quick-stat-item winrate">
          <div className="stat-icon">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{profileData.winRate}%</div>
            <div className="stat-label">{t('winRate', 'Win Rate')}</div>
          </div>
        </div>

        <div className="quick-stat-item streak">
          <div className="stat-icon">
            <Zap className="w-5 h-5" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{profileData.currentWinStreak}</div>
            <div className="stat-label">{t('currentStreak', 'Current Streak')}</div>
          </div>
        </div>

        {profileData.showBadges !== false && (
          <div className="quick-stat-item badges">
            <div className="stat-icon">
              <Award className="w-5 h-5" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{profileData.badgeCount}</div>
              <div className="stat-label">{t('badges', 'Badges')}</div>
            </div>
          </div>
        )}

        <div className="quick-stat-item matches">
          <div className="stat-icon">
            <Activity className="w-5 h-5" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{profileData.totalGames}</div>
            <div className="stat-label">{t('totalMatches', 'Total Matches')}</div>
          </div>
        </div>
      </div>

      {/* Game Mode Stats */}
      <div className="game-modes-section">
        <h4 className="section-title">{t('gameModeStats', 'Game Mode Statistics')}</h4>
        <div className="game-modes-grid">
          <div className="game-mode-card multiplayer">
            <div className="game-mode-header">
              <Target className="w-4 h-4" />
              <span>{t('multiplayer', 'Multiplayer')}</span>
            </div>
            <div className="game-mode-stats">
              <div className="mode-stat">
                <span className="stat-value">{profileData.multiplayerPlayed}</span>
                <span className="stat-label">{t('played', 'Played')}</span>
              </div>
              <div className="mode-stat">
                <span className="stat-value">{profileData.multiplayerWins}</span>
                <span className="stat-label">{t('wins', 'Wins')}</span>
              </div>
              <div className="mode-stat">
                <span className="stat-value">{profileData.multiplayerWinRate}%</span>
                <span className="stat-label">{t('winRate', 'Win Rate')}</span>
              </div>
            </div>
          </div>

          <div className="game-mode-card battle-royale">
            <div className="game-mode-header">
              <Users className="w-4 h-4" />
              <span>Battle Royale</span>
            </div>
            <div className="game-mode-stats">
              <div className="mode-stat">
                <span className="stat-value">{profileData.battleRoyalePlayed}</span>
                <span className="stat-label">{t('played', 'Played')}</span>
              </div>
              <div className="mode-stat">
                <span className="stat-value">{profileData.battleRoyaleWins}</span>
                <span className="stat-label">{t('wins', 'Wins')}</span>
              </div>
              <div className="mode-stat">
                <span className="stat-value">{profileData.battleRoyaleWinRate}%</span>
                <span className="stat-label">{t('winRate', 'Win Rate')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="account-info-section">
        <h4 className="section-title">{t('accountInformation', 'Account Information')}</h4>
        <div className="account-info-grid">
          <div className="info-item">
            <Calendar className="w-4 h-4" />
            <div className="info-content">
              <span className="info-label">{t('memberSince', 'Member Since')}</span>
              <span className="info-value">{formatDate(profileData.createdAt)}</span>
            </div>
          </div>
          
          <div className="info-item">
            <Activity className="w-4 h-4" />
            <div className="info-content">
              <span className="info-label">{t('accountAge', 'Account Age')}</span>
              <span className="info-value">
                {profileData.accountAge} {t('days', 'days')}
              </span>
            </div>
          </div>
          
          <div className="info-item">
            <div className={`status-indicator ${profileData.isOnline ? 'online' : 'offline'}`}>
              <div className="status-dot"></div>
            </div>
            <div className="info-content">
              <span className="info-label">
                {profileData.isOnline ? t('currentlyOnline', 'Currently Online') : t('lastSeen', 'Last Seen')}
              </span>
              <span className="info-value">
                {profileData.isOnline 
                  ? t('online', 'Online') 
                  : formatLastOnline(profileData.lastOnline)
                }
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Fun Stats */}
      {(profileData.fillerWins > 0 || profileData.maxWinStreak > 0) && (
        <div className="fun-stats-section">
          <h4 className="section-title">{t('funStats', 'Fun Statistics')}</h4>
          <div className="fun-stats-grid">
            {profileData.maxWinStreak > 0 && (
              <div className="fun-stat-item">
                <Zap className="w-4 h-4" />
                <div className="fun-stat-content">
                  <span className="fun-stat-value">{profileData.maxWinStreak}</span>
                  <span className="fun-stat-label">{t('bestWinStreak', 'Best Win Streak')}</span>
                </div>
              </div>
            )}
            
            {profileData.fillerWins > 0 && (
              <div className="fun-stat-item">
                <Star className="w-4 h-4" />
                <div className="fun-stat-content">
                  <span className="fun-stat-value">{profileData.fillerWins}</span>
                  <span className="fun-stat-label">{t('fillerWins', 'Filler Wins')}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicPlayerInfo;